import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/ui/data-table";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { TeamEmailService } from "@/gen/omnios/v1/team_email_connect.js";
import { transport } from "@/lib/grpc";
import { useBreadcrumbs } from "@/lib/use-breadcrumbs";
import { createClient } from "@connectrpc/connect";
import { createFileRoute } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import {
	CheckCircle2,
	Dice5,
	Loader2,
	Mail,
	MoreHorizontal,
	Pause,
	Play,
	Plus,
	Search,
	Trash2,
	XCircle,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

export const Route = createFileRoute("/settings/team-email")({
	component: TeamEmailSettingsPage,
});

const client = createClient(TeamEmailService, transport);

interface TeamEmailRow {
	id: string;
	email: string;
	status: string;
	channelId: string;
	visibility: string;
	createdAt: string;
}

function TeamEmailSettingsPage() {
	const { t } = useTranslation("settings");

	useBreadcrumbs([
		{ label: t("breadcrumb_settings"), href: "/settings" },
		{ label: t("team_email_breadcrumb") },
	]);

	const [emails, setEmails] = useState<TeamEmailRow[]>([]);
	const [loading, setLoading] = useState(true);
	const [createOpen, setCreateOpen] = useState(false);
	const [search, setSearch] = useState("");
	const [deleteTarget, setDeleteTarget] = useState<TeamEmailRow | null>(null);

	const fetchEmails = async () => {
		setLoading(true);
		try {
			const resp = await client.listTeamEmails({});
			setEmails(
				resp.teamEmails.map((te) => ({
					id: te.id,
					email: te.email,
					status: te.status,
					channelId: te.channelId,
					visibility: te.visibility,
					createdAt: te.createdAt
						? new Date(Number(te.createdAt.seconds) * 1000).toLocaleDateString(
								"ru-RU",
							)
						: "",
				})),
			);
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		fetchEmails();
	}, []);

	const handlePause = async (id: string) => {
		await client.pauseTeamEmail({ id });
		fetchEmails();
	};

	const handleResume = async (id: string) => {
		await client.resumeTeamEmail({ id });
		fetchEmails();
	};

	const handleDelete = async (id: string) => {
		await client.deleteTeamEmail({ id });
		setDeleteTarget(null);
		fetchEmails();
	};

	const columns: ColumnDef<TeamEmailRow, unknown>[] = [
		{
			accessorKey: "email",
			header: "Email",
			cell: ({ row }) => (
				<div className="flex items-center gap-2">
					<Mail className="h-4 w-4 text-muted-foreground" />
					<span className="text-sm font-medium">{row.original.email}</span>
				</div>
			),
		},
		{
			accessorKey: "status",
			header: t("team_email_col_status"),
			cell: ({ row }) => {
				const s = row.original.status;
				return (
					<Badge variant={s === "active" ? "default" : "secondary"}>
						{s === "active"
							? t("team_email_status_active")
							: t("team_email_status_paused")}
					</Badge>
				);
			},
		},
		{
			accessorKey: "createdAt",
			header: t("team_email_col_created"),
		},
		{
			id: "actions",
			header: "",
			cell: ({ row }) => {
				const te = row.original;
				return (
					<DropdownMenu>
						<DropdownMenuTrigger asChild>
							<Button variant="ghost" size="icon">
								<MoreHorizontal className="h-4 w-4" />
							</Button>
						</DropdownMenuTrigger>
						<DropdownMenuContent align="end">
							{te.status === "active" ? (
								<DropdownMenuItem onSelect={() => handlePause(te.id)}>
									<Pause className="h-4 w-4" />
									{t("team_email_pause")}
								</DropdownMenuItem>
							) : (
								<DropdownMenuItem onSelect={() => handleResume(te.id)}>
									<Play className="h-4 w-4" />
									{t("team_email_resume")}
								</DropdownMenuItem>
							)}
							<DropdownMenuItem
								className="text-destructive focus:text-destructive"
								onSelect={() => setDeleteTarget(te)}
							>
								<Trash2 className="h-4 w-4" />
								{t("common:delete")}
							</DropdownMenuItem>
						</DropdownMenuContent>
					</DropdownMenu>
				);
			},
		},
	];

	const filtered = emails.filter(
		(e) => !search || e.email.toLowerCase().includes(search.toLowerCase()),
	);

	return (
		<div className="space-y-6">
			<div className="flex items-center justify-between gap-4">
				<div className="relative">
					<Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
					<Input
						placeholder={t("team_email_search_placeholder")}
						value={search}
						onChange={(e) => setSearch(e.target.value)}
						className="pl-9 h-9 w-56"
					/>
				</div>
				<Button size="sm" onClick={() => setCreateOpen(true)}>
					<Plus className="h-4 w-4" />
					{t("common:add")}
				</Button>
			</div>

			{loading ? (
				<div className="flex h-32 items-center justify-center text-sm text-muted-foreground">
					{t("common:loading")}
				</div>
			) : filtered.length === 0 ? (
				<div className="flex h-32 items-center justify-center rounded-md border border-dashed text-sm text-muted-foreground">
					{t("team_email_no_mailboxes")}
				</div>
			) : (
				<DataTable columns={columns} data={filtered} />
			)}

			<CreateTeamEmailDialog
				open={createOpen}
				onOpenChange={setCreateOpen}
				onCreated={fetchEmails}
			/>

			<AlertDialog
				open={!!deleteTarget}
				onOpenChange={(open) => {
					if (!open) setDeleteTarget(null);
				}}
			>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>{t("team_email_delete_title")}</AlertDialogTitle>
						<AlertDialogDescription>
							{t("team_email_delete_desc", { email: deleteTarget?.email })}
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>{t("common:cancel")}</AlertDialogCancel>
						<AlertDialogAction
							onClick={() => {
								if (deleteTarget) handleDelete(deleteTarget.id);
							}}
							className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
						>
							{t("common:delete")}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</div>
	);
}

function generateRandomSuffix(): string {
	const chars = "abcdefghijklmnopqrstuvwxyz0123456789";
	let result = "";
	for (let i = 0; i < 6; i++) {
		result += chars.charAt(Math.floor(Math.random() * chars.length));
	}
	return result;
}

function CreateTeamEmailDialog({
	open,
	onOpenChange,
	onCreated,
}: {
	open: boolean;
	onOpenChange: (v: boolean) => void;
	onCreated: () => void;
}) {
	const { t } = useTranslation("settings");
	const [localPart, setLocalPart] = useState("");
	const [domain, setDomain] = useState("");
	const [domainLoading, setDomainLoading] = useState(true);
	const [previewing, setPreviewing] = useState(false);
	const [preview, setPreview] = useState<{
		fullEmail: string;
		available: boolean;
	} | null>(null);
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		if (open) {
			setDomainLoading(true);
			client
				.getTeamEmailDomain({})
				.then((resp) => setDomain(resp.domain))
				.catch(() => setDomain(""))
				.finally(() => setDomainLoading(false));
		}
	}, [open]);

	useEffect(() => {
		if (!localPart.trim()) {
			setPreview(null);
			return;
		}
		setPreviewing(true);
		const timer = setTimeout(() => {
			client
				.previewTeamEmail({
					localPart: localPart.trim(),
				})
				.then((resp) =>
					setPreview({
						fullEmail: resp.fullEmail,
						available: resp.available,
					}),
				)
				.catch(() => setPreview(null))
				.finally(() => setPreviewing(false));
		}, 300);
		return () => clearTimeout(timer);
	}, [localPart]);

	const handleGenerateSuffix = () => {
		const base = localPart.replace(/-[a-z0-9]{6}$/, "");
		setLocalPart(`${base || "support"}-${generateRandomSuffix()}`);
	};

	const handleSubmit = async () => {
		if (!localPart.trim() || !domain) return;
		const fullEmail = `${localPart.trim()}@${domain}`;
		setSaving(true);
		setError(null);
		try {
			await client.createTeamEmail({
				email: fullEmail,
				visibility: "public",
				allowedDomains: [],
			});
			setLocalPart("");
			setPreview(null);
			onOpenChange(false);
			onCreated();
		} catch (e) {
			setError(e instanceof Error ? e.message : t("team_email_create_error"));
		} finally {
			setSaving(false);
		}
	};

	const resetForm = () => {
		setLocalPart("");
		setPreview(null);
		setError(null);
	};

	return (
		<Dialog
			open={open}
			onOpenChange={(v) => {
				if (!v) resetForm();
				onOpenChange(v);
			}}
		>
			<DialogContent className="sm:max-w-md">
				<DialogHeader>
					<DialogTitle>{t("team_email_create_title")}</DialogTitle>
					<DialogDescription>{t("team_email_create_desc")}</DialogDescription>
				</DialogHeader>
				<div className="space-y-4 py-2">
					<div className="space-y-2">
						<Label htmlFor="email-local">{t("team_email_label_address")}</Label>
						<div className="flex items-center gap-2">
							<div className="flex flex-1 items-center gap-1">
								<Input
									id="email-local"
									placeholder="support"
									value={localPart}
									onChange={(e) => setLocalPart(e.target.value.toLowerCase())}
									className="flex-1"
								/>
								<Button
									type="button"
									variant="ghost"
									size="icon"
									onClick={handleGenerateSuffix}
									title={t("team_email_generate_suffix")}
								>
									<Dice5 className="h-4 w-4" />
								</Button>
							</div>
							{domainLoading ? (
								<span className="text-muted-foreground text-sm">
									<Loader2 className="h-4 w-4 animate-spin" />
								</span>
							) : (
								<span className="text-muted-foreground text-sm whitespace-nowrap">
									@{domain || "..."}
								</span>
							)}
						</div>
						{previewing && (
							<p className="text-sm text-muted-foreground flex items-center gap-1">
								<Loader2 className="h-3 w-3 animate-spin" />
								{t("team_email_checking")}
							</p>
						)}
						{preview && !previewing && (
							<p className="text-sm flex items-center gap-1.5">
								{preview.available ? (
									<>
										<CheckCircle2 className="h-4 w-4 text-green-500" />
										<span className="text-muted-foreground">
											{t("team_email_available", { email: preview.fullEmail })}
										</span>
									</>
								) : (
									<>
										<XCircle className="h-4 w-4 text-destructive" />
										<span className="text-muted-foreground">
											{t("team_email_taken", { email: preview.fullEmail })}
										</span>
									</>
								)}
							</p>
						)}
					</div>
				</div>
				{error && <p className="text-sm text-destructive">{error}</p>}
				<DialogFooter>
					<Button
						onClick={handleSubmit}
						disabled={
							!localPart.trim() ||
							!domain ||
							saving ||
							(preview !== null && !preview.available)
						}
					>
						{saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
						{saving ? t("team_email_creating") : t("team_email_connect")}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
